import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Box,
  Slider,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Switch,
  Alert,
  InputAdornment,
  Card,
  CardContent,
  Autocomplete
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PaletteIcon from '@mui/icons-material/Palette';
import { getCartSettings, saveCartSettings as saveCartSettingsAPI } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

// ✅ Popüler Font Aileleri Listesi
const FONT_FAMILIES = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Ubuntu',
  'Nunito',
  'Playfair Display',
  'Merriweather',
  'PT Sans',
  'Source Sans Pro',
  'Oswald',
  'Slabo 27px',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Courier New',
  'Trebuchet MS',
  'Comic Sans MS',
  'Impact',
];

interface CartSettings {
  orderType: 'product' | 'food'; // Yeni eklenen sipariş türü
  pageTitle: string;
  productCard: {
    size: {
      width: number;
      height: number;
    };
    productName: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
    price: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
    stockStatus: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
  };
  orderSummary: {
    title: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
    labels: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
    values: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
    total: {
      fontSize: number;
      color: string;
      fontWeight: number;
      fontFamily: string;
    };
  };
  shipping: {
    enabled: boolean;
    freeShippingThreshold: number;
    shippingCost: number;
    currency: string;
    displayName: string; // "Kargo" veya "Teslimat Ücreti"
  };
  completeOrderButton: {
    backgroundColor: string;
    hoverBackgroundColor: string;
    textColor: string;
    text: string;
    fontSize: number;
    fontWeight: number;
    borderRadius: number;
    buttonStyle: 'default' | 'rounded' | 'square';
  };
  // Yemek siparişleri için özel ayarlar
  foodOrderSettings?: {
    allowOrderNotes: boolean;
    estimatedPrepTime: string; // "15-20 dakika" gibi
    tableNumberRequired: boolean;
  };
}

const createDefaultCartSettings = (t: (key: string, options?: any) => string): CartSettings => ({
  orderType: 'product', // Varsayılan olarak ürün siparişleri
  pageTitle: t('ecommerceCartSettings.defaults.pageTitle'),
  productCard: {
    size: {
      width: 120,
      height: 120,
    },
    productName: {
      fontSize: 16,
      color: '#1f2937',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
    price: {
      fontSize: 18,
      color: '#1f2937',
      fontWeight: 700,
      fontFamily: 'Inter',
    },
    stockStatus: {
      fontSize: 14,
      color: '#16a34a',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
  },
  orderSummary: {
    title: {
      fontSize: 18,
      color: '#1f2937',
      fontWeight: 600,
      fontFamily: 'Inter',
    },
    labels: {
      fontSize: 14,
      color: '#6b7280',
      fontWeight: 400,
      fontFamily: 'Inter',
    },
    values: {
      fontSize: 14,
      color: '#1f2937',
      fontWeight: 500,
      fontFamily: 'Inter',
    },
    total: {
      fontSize: 18,
      color: '#2563eb',
      fontWeight: 700,
      fontFamily: 'Inter',
    },
  },
  shipping: {
    enabled: true,
    freeShippingThreshold: 150,
    shippingCost: 15,
    currency: '₺',
    displayName: t('ecommerceCartSettings.shipping.defaultDisplayName'),
  },
  completeOrderButton: {
    backgroundColor: '#2563eb',
    hoverBackgroundColor: '#1d4ed8',
    textColor: '#ffffff',
    text: t('ecommerceCartSettings.defaults.completeOrderButtonText'),
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 8,
    buttonStyle: 'default',
  },
  // Yemek siparişleri için varsayılan ayarlar
  foodOrderSettings: {
    allowOrderNotes: true,
    estimatedPrepTime: t('ecommerceCartSettings.defaults.estimatedPreparationTime'),
    tableNumberRequired: true,
  },
});

interface CartSettingsComponentProps {
  companyId: string | null;
  configId: string; // ✅ YENİ: SQL için configId
}

const CartSettingsComponent: React.FC<CartSettingsComponentProps> = ({ companyId, configId }) => {
  const { t } = useTranslation('salesSystem');
  const translate = useCallback((key: string, options?: Record<string, unknown>) => {
    return t(key, options as any) as string;
  }, [t]);
  const [settings, setSettings] = useState<CartSettings>(() => createDefaultCartSettings(translate));
  const [cartSettingsId, setCartSettingsId] = useState<string | null>(null); // ✅ SQL ID
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef<CartSettings>(settings);
  const cartSettingsIdRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced autosave stale closure'a düşmesin diye her zaman güncel state'i ref'te tut
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    cartSettingsIdRef.current = cartSettingsId;
  }, [cartSettingsId]);

  // ✅ SQL'den ayarları yükle
  useEffect(() => {
    const loadCartSettingsFromSQL = async () => {
      if (!companyId || !configId) return;

      setLoading(true);
      try {
        console.log('🛒 [CART SETTINGS] SQL den yükleniyor - ConfigId:', configId);
        const response = await getCartSettings(companyId, configId); // ✅ companyId eklendi

        if (response.success && response.data) {
          const data = response.data;
          setCartSettingsId(data.id); // SQL ID'yi kaydet
          cartSettingsIdRef.current = data.id;
          const defaultCartSettings = createDefaultCartSettings(translate);

          // JSON alanlarını parse et
          const productCard = data.productCardJson ? JSON.parse(data.productCardJson) : defaultCartSettings.productCard;
          const orderSummary = data.orderSummaryJson ? JSON.parse(data.orderSummaryJson) : defaultCartSettings.orderSummary;
          const shipping = data.shippingJson ? JSON.parse(data.shippingJson) : defaultCartSettings.shipping;
          const completeOrderButton = data.completeOrderButtonJson ? JSON.parse(data.completeOrderButtonJson) : defaultCartSettings.completeOrderButton;
          const foodOrderSettings = data.foodOrderSettingsJson ? JSON.parse(data.foodOrderSettingsJson) : defaultCartSettings.foodOrderSettings;

          const loaded: CartSettings = {
            orderType: data.orderType || 'product',
            pageTitle: data.pageTitle || defaultCartSettings.pageTitle,
            productCard,
            orderSummary,
            shipping,
            completeOrderButton,
            foodOrderSettings,
          };
          settingsRef.current = loaded;
          setSettings(loaded);

          console.log('✅ [CART SETTINGS] SQL den başarıyla yüklendi');
        } else {
          // Veri yoksa varsayılan ayarları kullan
          console.log('ℹ️ [CART SETTINGS] SQL de veri yok, varsayılan ayarlar kullanılıyor');
          const defaults = createDefaultCartSettings(translate);
          settingsRef.current = defaults;
          setSettings(defaults);
        }
      } catch (err) {
        console.error('❌ [CART SETTINGS] SQL den yükleme hatası:', err);
        setError(translate('ecommerceCartSettings.errors.loadFailed'));
        const defaults = createDefaultCartSettings(translate);
        settingsRef.current = defaults;
        setSettings(defaults);
      } finally {
        setLoading(false);
      }
    };

    loadCartSettingsFromSQL();
  }, [companyId, configId, translate]);

  // ✅ SQL'e ayarları kaydet — her zaman settingsRef (güncel değer)
  const saveCartSettings = useCallback(async (override?: CartSettings) => {
    if (!companyId || !configId) return;

    const current = override ?? settingsRef.current;
    setSaving(true);
    setError(null);

    try {
      console.log('💾 [CART SETTINGS] SQL e kaydediliyor...', {
        buttonColor: current.completeOrderButton?.backgroundColor,
        totalColor: current.orderSummary?.total?.color,
      });

      const payload = {
        id: cartSettingsIdRef.current,
        configId: configId,
        companyId: companyId,
        orderType: current.orderType,
        pageTitle: current.pageTitle,
        productCardJson: JSON.stringify(current.productCard),
        orderSummaryJson: JSON.stringify(current.orderSummary),
        shippingJson: JSON.stringify(current.shipping),
        completeOrderButtonJson: JSON.stringify(current.completeOrderButton),
        foodOrderSettingsJson: JSON.stringify(current.foodOrderSettings),
      };

      const response = await saveCartSettingsAPI(payload);

      if (response.success) {
        if (!cartSettingsIdRef.current && response.data?.id) {
          setCartSettingsId(response.data.id);
          cartSettingsIdRef.current = response.data.id;
        }
        console.log('✅ [CART SETTINGS] SQL e başarıyla kaydedildi');
        notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      }
    } catch (err) {
      console.error('❌ [CART SETTINGS] SQL e kaydetme hatası:', err);
      setError(translate('ecommerceCartSettings.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [companyId, configId, t, translate]);

  // ✅ AUTO-SAVE: debounce — timer tetiklenince ref'teki güncel ayarı kaydet
  const autoSaveCartSettings = useCallback((override?: CartSettings) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      void saveCartSettings(override);
    }, 400);
  }, [saveCartSettings]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // ✅ Ayar değişikliği (AUTO-SAVE ile)
  const handleSettingChange = (path: string[], value: any) => {
    setSettings((prev) => {
      const newSettings = { ...prev };
      let current: any = newSettings;

      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = { ...current[path[i]] };
        current = current[path[i]];
      }

      current[path[path.length - 1]] = value;
      settingsRef.current = newSettings;
      autoSaveCartSettings(newSettings);
      return newSettings;
    });
  };

  // Varsayılana sıfırla
  const resetToDefault = () => {
    const defaults = createDefaultCartSettings(translate);
    settingsRef.current = defaults;
    setSettings(defaults);
    autoSaveCartSettings(defaults);
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography>{t('ecommerceCartSettings.common.loading')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ShoppingCartIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('ecommerceCartSettings.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RestartAltIcon />}
            onClick={resetToDefault}
          >
            {t('ecommerceCartSettings.actions.resetToDefault')}
          </Button>
          {/* ✅ AUTO-SAVE Göstergesi */}
          {saving && (
            <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              💾 {t('ecommerceCartSettings.common.saving')}
            </Typography>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Marka rengi Genel Ayarlar'a taşındı — buradan bilgi + yönlendirme */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<PaletteIcon />} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {t('ecommerceCartSettings.brandTheme.movedTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('ecommerceCartSettings.brandTheme.movedDescription')}
            </Typography>
          </Alert>
        </Grid>

        {/* Sipariş Türü Seçimi */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ border: '2px solid #e97444', backgroundColor: '#fff5f0' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#e97444' }}>
                🛒 {t('ecommerceCartSettings.orderType.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('ecommerceCartSettings.orderType.description')}
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={settings.orderType}
                  onChange={(e) => handleSettingChange(['orderType'], e.target.value)}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel 
                    value="product" 
                    control={<Radio sx={{ color: '#e97444', '&.Mui-checked': { color: '#e97444' } }} />} 
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="bold">📦 {t('ecommerceCartSettings.orderType.productOrdersTitle')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ecommerceCartSettings.orderType.productOrdersDescription')}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="food" 
                    control={<Radio sx={{ color: '#e97444', '&.Mui-checked': { color: '#e97444' } }} />} 
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="bold">🍽️ {t('ecommerceCartSettings.orderType.foodOrdersTitle')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('ecommerceCartSettings.orderType.foodOrdersDescription')}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Sayfa Başlığı */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceCartSettings.pageTitle.sectionTitle')}
              </Typography>
              <TextField
                fullWidth
                label={t('ecommerceCartSettings.pageTitle.inputLabel')}
                value={settings.pageTitle}
                onChange={(e) => handleSettingChange(['pageTitle'], e.target.value)}
                variant="outlined"
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Ürün Kartı Ayarları */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceCartSettings.sections.productCardSettings')}
              </Typography>
              
              {/* Kart Boyutu */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{t('ecommerceCartSettings.sections.cardSize')}</Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      {t('ecommerceCartSettings.sections.width')}: {settings.productCard.size.width}px
                    </Typography>
                    <Slider
                      value={settings.productCard.size.width}
                      onChange={(e, newValue) => handleSettingChange(['productCard', 'size', 'width'], newValue as number)}
                      aria-labelledby="width-slider"
                      valueLabelDisplay="auto"
                      step={10}
                      min={80}
                      max={300}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      {t('ecommerceCartSettings.sections.height')}: {settings.productCard.size.height}px
                    </Typography>
                    <Slider
                      value={settings.productCard.size.height}
                      onChange={(e, newValue) => handleSettingChange(['productCard', 'size', 'height'], newValue as number)}
                      aria-labelledby="height-slider"
                      valueLabelDisplay="auto"
                      step={10}
                      min={80}
                      max={300}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Ürün Adı Stilleri */}
              <Typography variant="subtitle2" gutterBottom>{t('ecommerceCartSettings.sections.productNameStyles')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.productCard.productName.fontSize}
                    onChange={(e) => handleSettingChange(['productCard', 'productName', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.color')}
                    value={settings.productCard.productName.color}
                    onChange={(e) => handleSettingChange(['productCard', 'productName', 'color'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.productCard.productName.color}
                            onChange={(e) => handleSettingChange(['productCard', 'productName', 'color'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontWeight')}
                    type="number"
                    inputProps={{ min: 100, max: 900, step: 100 }}
                    value={settings.productCard.productName.fontWeight}
                    onChange={(e) => handleSettingChange(['productCard', 'productName', 'fontWeight'], parseInt(e.target.value))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    options={FONT_FAMILIES}
                    value={settings.productCard.productName.fontFamily}
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        handleSettingChange(['productCard', 'productName', 'fontFamily'], newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('ecommerceCartSettings.fields.fontFamily')}
                        size="small"
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue && inputValue !== settings.productCard.productName.fontFamily) {
                            handleSettingChange(['productCard', 'productName', 'fontFamily'], inputValue);
                          }
                        }}
                      />
                    )}
                    freeSolo
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Fiyat Stilleri */}
              <Typography variant="subtitle2" gutterBottom>{t('ecommerceCartSettings.sections.priceStyles')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.productCard.price.fontSize}
                    onChange={(e) => handleSettingChange(['productCard', 'price', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.color')}
                    value={settings.productCard.price.color}
                    onChange={(e) => handleSettingChange(['productCard', 'price', 'color'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.productCard.price.color}
                            onChange={(e) => handleSettingChange(['productCard', 'price', 'color'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontWeight')}
                    type="number"
                    inputProps={{ min: 100, max: 900, step: 100 }}
                    value={settings.productCard.price.fontWeight}
                    onChange={(e) => handleSettingChange(['productCard', 'price', 'fontWeight'], parseInt(e.target.value))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    options={FONT_FAMILIES}
                    value={settings.productCard.price.fontFamily}
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        handleSettingChange(['productCard', 'price', 'fontFamily'], newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('ecommerceCartSettings.fields.fontFamily')}
                        size="small"
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue && inputValue !== settings.productCard.price.fontFamily) {
                            handleSettingChange(['productCard', 'price', 'fontFamily'], inputValue);
                          }
                        }}
                      />
                    )}
                    freeSolo
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Stok Durumu Stilleri */}
              <Typography variant="subtitle2" gutterBottom>{t('ecommerceCartSettings.sections.stockStatusStyles')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.productCard.stockStatus.fontSize}
                    onChange={(e) => handleSettingChange(['productCard', 'stockStatus', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.color')}
                    value={settings.productCard.stockStatus.color}
                    onChange={(e) => handleSettingChange(['productCard', 'stockStatus', 'color'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.productCard.stockStatus.color}
                            onChange={(e) => handleSettingChange(['productCard', 'stockStatus', 'color'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontWeight')}
                    type="number"
                    inputProps={{ min: 100, max: 900, step: 100 }}
                    value={settings.productCard.stockStatus.fontWeight}
                    onChange={(e) => handleSettingChange(['productCard', 'stockStatus', 'fontWeight'], parseInt(e.target.value))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    options={FONT_FAMILIES}
                    value={settings.productCard.stockStatus.fontFamily}
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        handleSettingChange(['productCard', 'stockStatus', 'fontFamily'], newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('ecommerceCartSettings.fields.fontFamily')}
                        size="small"
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue && inputValue !== settings.productCard.stockStatus.fontFamily) {
                            handleSettingChange(['productCard', 'stockStatus', 'fontFamily'], inputValue);
                          }
                        }}
                      />
                    )}
                    freeSolo
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sipariş Özeti Ayarları */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceCartSettings.sections.orderSummarySettings')}
              </Typography>
              
              {/* Başlık Stilleri */}
              <Typography variant="subtitle2" gutterBottom>{t('ecommerceCartSettings.sections.titleStyles')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.orderSummary.title.fontSize}
                    onChange={(e) => handleSettingChange(['orderSummary', 'title', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.color')}
                    value={settings.orderSummary.title.color}
                    onChange={(e) => handleSettingChange(['orderSummary', 'title', 'color'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.orderSummary.title.color}
                            onChange={(e) => handleSettingChange(['orderSummary', 'title', 'color'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Toplam Yazısı Stilleri */}
              <Typography variant="subtitle2" gutterBottom>{t('ecommerceCartSettings.sections.totalTextStyles')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.orderSummary.total.fontSize}
                    onChange={(e) => handleSettingChange(['orderSummary', 'total', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.color')}
                    value={settings.orderSummary.total.color}
                    onChange={(e) => handleSettingChange(['orderSummary', 'total', 'color'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.orderSummary.total.color}
                            onChange={(e) => handleSettingChange(['orderSummary', 'total', 'color'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Kargo/Teslimat Ücreti Ayarları - Hem ürün hem yemek siparişlerinde göster */}
        <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('ecommerceCartSettings.shipping.settingsTitle', { displayName: settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName') })}
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.shipping.enabled}
                      onChange={(e) => handleSettingChange(['shipping', 'enabled'], e.target.checked)}
                    />
                  }
                  label={t('ecommerceCartSettings.shipping.systemActive', { displayName: settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName') })}
                  sx={{ mb: 2 }}
                />

                {settings.shipping.enabled && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <FormLabel component="legend">{t('ecommerceCartSettings.shipping.displayName')}</FormLabel>
                        <RadioGroup
                          row
                          value={settings.shipping.displayName || 'Kargo'}
                          onChange={(e) => handleSettingChange(['shipping', 'displayName'], e.target.value)}
                        >
                          <FormControlLabel value="Kargo" control={<Radio />} label={t('ecommerceCartSettings.shipping.shippingLabel')} />
                          <FormControlLabel value="Teslimat Ücreti" control={<Radio />} label={t('ecommerceCartSettings.shipping.deliveryFeeLabel')} />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceCartSettings.shipping.freeThreshold', { displayName: settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName') })}
                        type="number"
                        value={settings.shipping.freeShippingThreshold}
                        onChange={(e) => handleSettingChange(['shipping', 'freeShippingThreshold'], parseInt(e.target.value))}
                        InputProps={{ 
                          endAdornment: <InputAdornment position="end">{settings.shipping.currency}</InputAdornment> 
                        }}
                        size="small"
                        helperText={t('ecommerceCartSettings.shipping.freeThresholdHelper', { displayName: (settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName')).toLowerCase() })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label={settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName')}
                        type="number"
                        value={settings.shipping.shippingCost}
                        onChange={(e) => handleSettingChange(['shipping', 'shippingCost'], parseInt(e.target.value))}
                        InputProps={{ 
                          endAdornment: <InputAdornment position="end">{settings.shipping.currency}</InputAdornment> 
                        }}
                        size="small"
                        helperText={t('ecommerceCartSettings.shipping.belowThresholdHelper', { displayName: (settings.shipping.displayName || t('ecommerceCartSettings.shipping.defaultDisplayName')).toLowerCase() })}
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

        {/* Yemek Siparişi Ayarları - Sadece yemek siparişlerinde göster */}
        {settings.orderType === 'food' && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ border: '2px solid #ff8c00', backgroundColor: '#fff8f0' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#ff8c00' }}>
                  🍽️ {t('ecommerceCartSettings.foodOrder.settingsTitle')}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.foodOrderSettings?.allowOrderNotes || false}
                          onChange={(e) => handleSettingChange(['foodOrderSettings', 'allowOrderNotes'], e.target.checked)}
                          sx={{ 
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff8c00' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff8c00' }
                          }}
                        />
                      }
                      label={t('ecommerceCartSettings.foodOrder.orderNoteSupport')}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.foodOrderSettings?.tableNumberRequired || false}
                          onChange={(e) => handleSettingChange(['foodOrderSettings', 'tableNumberRequired'], e.target.checked)}
                          sx={{ 
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff8c00' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff8c00' }
                          }}
                        />
                      }
                      label={t('ecommerceCartSettings.foodOrder.tableNumberRequired')}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('ecommerceCartSettings.foodOrder.estimatedPreparationTime')}
                      value={settings.foodOrderSettings?.estimatedPrepTime || ''}
                      onChange={(e) => handleSettingChange(['foodOrderSettings', 'estimatedPrepTime'], e.target.value)}
                      size="small"
                      helperText={t('ecommerceCartSettings.foodOrder.preparationTimeHelper')}
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#ff8c00',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#ff8c00',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Complete Order Button */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceCartSettings.completeOrderButton.sectionTitle')}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.completeOrderButton.buttonText')}
                    value={settings.completeOrderButton.text}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'text'], e.target.value)}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.completeOrderButton.backgroundColor')}
                    value={settings.completeOrderButton.backgroundColor}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'backgroundColor'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.completeOrderButton.backgroundColor}
                            onChange={(e) => handleSettingChange(['completeOrderButton', 'backgroundColor'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.completeOrderButton.hoverColor')}
                    value={settings.completeOrderButton.hoverBackgroundColor}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'hoverBackgroundColor'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.completeOrderButton.hoverBackgroundColor}
                            onChange={(e) => handleSettingChange(['completeOrderButton', 'hoverBackgroundColor'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.completeOrderButton.textColor')}
                    value={settings.completeOrderButton.textColor}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'textColor'], e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={settings.completeOrderButton.textColor}
                            onChange={(e) => handleSettingChange(['completeOrderButton', 'textColor'], e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.fields.fontSize')}
                    type="number"
                    value={settings.completeOrderButton.fontSize}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'fontSize'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceCartSettings.completeOrderButton.borderRadius')}
                    type="number"
                    value={settings.completeOrderButton.borderRadius}
                    onChange={(e) => handleSettingChange(['completeOrderButton', 'borderRadius'], parseInt(e.target.value))}
                    InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">{t('ecommerceCartSettings.completeOrderButton.buttonStyle')}</FormLabel>
                    <RadioGroup
                      row
                      value={settings.completeOrderButton.buttonStyle}
                      onChange={(e) => handleSettingChange(['completeOrderButton', 'buttonStyle'], e.target.value)}
                    >
                      <FormControlLabel value="default" control={<Radio />} label={t('ecommerceCartSettings.completeOrderButton.styles.default')} />
                      <FormControlLabel value="rounded" control={<Radio />} label={t('ecommerceCartSettings.completeOrderButton.styles.rounded')} />
                      <FormControlLabel value="square" control={<Radio />} label={t('ecommerceCartSettings.completeOrderButton.styles.square')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CartSettingsComponent;
